// Datas só para exibição (Parte 14.1): Luxon no fuso do site ativo; o fuso aparece quando difere do navegador.
// Cálculo clínico (janelas, slots) nunca acontece aqui (regra 1 da Parte 0.3).
import { DateTime, type DateTimeFormatOptions } from 'luxon';

export interface DateFormatOptions {
  /** Fuso do site ativo (IANA). `null` = fuso do navegador. */
  readonly zone: string | null;
  /** Locale BCP-47 (`pt-BR` | `en-US`). */
  readonly locale: string;
  /** Fuso do navegador (injetável para testes); padrão `DateTime.local().zoneName`. */
  readonly browserZone?: string;
}

export function browserTimeZone(): string {
  return DateTime.local().zoneName;
}

/** Verdadeiro quando o fuso do site difere do fuso do navegador (exibir sufixo). */
export function showsZone(zone: string | null, browserZone: string = browserTimeZone()): boolean {
  return zone !== null && zone !== browserZone;
}

function toDateTime(value: string | Date, zone: string | null, locale: string): DateTime {
  const base =
    typeof value === 'string'
      ? DateTime.fromISO(value, { setZone: false })
      : DateTime.fromJSDate(value);
  const zoned = zone ? base.setZone(zone) : base.toLocal();
  return zoned.setLocale(locale);
}

function formatWithZone(
  value: string | Date,
  options: DateFormatOptions,
  format: DateTimeFormatOptions,
): string {
  const dt = toDateTime(value, options.zone, options.locale);
  if (!dt.isValid) {
    return '';
  }
  const text = dt.toLocaleString(format);
  if (showsZone(options.zone, options.browserZone ?? browserTimeZone())) {
    // Deslocamento numérico (`GMT-4`): determinístico entre versões do ICU (abreviações como `AMT` variam).
    return `${text} (GMT${dt.toFormat('Z')})`;
  }
  return text;
}

/** Data e hora curtas (ex.: `23/09/2026, 10:02 (GMT-4)`). */
export function formatDateTime(value: string | Date, options: DateFormatOptions): string {
  return formatWithZone(value, options, DateTime.DATETIME_SHORT);
}

/** Só a data (sem sufixo de fuso: datas civis não mudam de nome). */
export function formatDate(value: string | Date, options: DateFormatOptions): string {
  const dt = toDateTime(value, options.zone, options.locale);
  return dt.isValid ? dt.toLocaleString(DateTime.DATE_MED) : '';
}

/** Só a hora, com sufixo de fuso quando difere. */
export function formatTime(value: string | Date, options: DateFormatOptions): string {
  return formatWithZone(value, options, DateTime.TIME_SIMPLE);
}

/** Tempo relativo localizado (`há 3 dias`, `in 2 hours`). */
export function formatRelative(
  value: string | Date,
  options: DateFormatOptions & { readonly now?: string | Date },
): string {
  const dt = toDateTime(value, options.zone, options.locale);
  if (!dt.isValid) {
    return '';
  }
  const base = options.now ? toDateTime(options.now, options.zone, options.locale) : undefined;
  return dt.toRelative(base ? { base } : {}) ?? '';
}
